import { config, SavePasswordMode } from '../../AuthConfig';
import CredentialsStore from './CredentialsStore';
import { StringMap } from '../../../utils/TypeDefinitions';
import { SentryLogger } from '../../../sentryLogging/SentryLogger';
import { Logger } from '../../Logger';
import { AotCategory, logConstants } from '../../../utils/Constants';
import { ParsedRequirementNode } from '../../../Common/Parsers/RequirementNodeParser';
import AuthForm from './AuthForm';
import { environment } from '../../../auth/environment';
import PrePopulatedCredentialsManager from './PrePopulatedCredentialsManager';
import { storageConstants } from '../../../auth/AuthConstants';
import NativeBridgeWrapper from '../NativeBridgeWrapper';

export default class CredentialsManager {
	private readonly allowSavedCredentials: boolean;
	private storedCredentials: StringMap = {};
	private areCredentialsStored = false;
	private authForm: AuthForm;
	private prepopulatedCredentialsManager: PrePopulatedCredentialsManager;
	private credentialsStore: CredentialsStore;

	private constructor(
		readonly authServiceUrl: string,
		readonly referenceUri: string,
		readonly requirements: ParsedRequirementNode[]
	) {
		this.allowSavedCredentials = CredentialsManager.canSaveCredentials(
			this.authServiceUrl
		);

		this.authForm = new AuthForm(this.requirements, this.allowSavedCredentials);
	}

	static async createInstance(
		authServiceUrl: string,
		referenceUri: string,
		requirements: ParsedRequirementNode[]
	): Promise<CredentialsManager> {
		const credentialsManager = new CredentialsManager(
			authServiceUrl,
			referenceUri,
			requirements
		);
		await credentialsManager.init();
		return credentialsManager;
	}

	private async init(): Promise<void> {
		this.prepopulatedCredentialsManager =
			await PrePopulatedCredentialsManager.createInstance(
				this.authServiceUrl,
				this.referenceUri,
				this.authForm
			);

		this.credentialsStore = await CredentialsStore.createInstance(
			storageConstants.CREDENTIALS_TABLE
		);

		if (this.allowSavedCredentials) {
			this.storedCredentials = await this.credentialsStore.read(
				this.authServiceUrl,
				this.referenceUri
			);
			this.areCredentialsStored =
				this.storedCredentials && Object.entries(this.storedCredentials).length !== 0;
		}

		await this.eraseInvalidStoredCredentials();
	}

	// Done on AML init to ensure credentials are immediately deleted, not just when login form is shown
	static async eraseCredentialsNotAllowedByConfig(): Promise<void> {
		try {
			await PrePopulatedCredentialsManager.eraseCredentialsNotAllowedByConfig();

			const credentialsStore = await CredentialsStore.createInstance(
				storageConstants.CREDENTIALS_TABLE
			);
			if (
				config.savePasswordMode === SavePasswordMode.Never ||
				!config.isUnifiedAuthenticationEnabled
			) {
				await credentialsStore.eraseAll();
			} else if (config.savePasswordMode === SavePasswordMode.SecureOnly) {
				const keys = await credentialsStore.getAllKeys();
				if (keys) {
					for (const key of keys) {
						if (!this.canSaveCredentials(key.authServiceUrl)) {
							await credentialsStore.erase(key.authServiceUrl, key.referenceUri);
						}
					}
				}
			}
		} catch (e) {
			SentryLogger.getInstance().logError(e);
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error in CredentialsManager eraseCredentialsNotAllowedByConfig: ' + e.message
			);
		}
	}

	static async eraseCredentialsForStore(referenceUri: string): Promise<void> {
		try {
			await PrePopulatedCredentialsManager.eraseCredentialsForStore(referenceUri);

			const credentialsStore = await CredentialsStore.createInstance(
				storageConstants.CREDENTIALS_TABLE
			);
			await credentialsStore.eraseForStore(referenceUri);
		} catch (e) {
			SentryLogger.getInstance().logError(e);
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error in CredentialsManager eraseCredentialsForStore: ' + e.message
			);
		}
	}

	/**
	 * Returns saved credentials in encoded format to pre-populate in the UI,
	 * e.g.: "domain=domain&username=userName" or "username=domain%5CuserName" or "username=user%40domain.com"
	 */
	getPrepopulatedCredentials(): string {
		return this.prepopulatedCredentialsManager.getPrepopulatedCredentials();
	}

	/**
	 * Returns POST data constructed from saved credentials in encoded format.
	 * example: "StateContext=&loginBtn=Log%20On&password=pw&saveCredentials=true&username=domain\name"
	 */
	async getPostData(stateContext: string): Promise<string> {
		// No need to check for allowSavedCredentials, since invalid credentials are cleared on AML init.
		if (!this.areCredentialsStored || !this.authForm.getSaveCredentialsID()) {
			return null;
		}

		try {
			let postData = `StateContext=${encodeURIComponent(stateContext)}`;

			let retrievedCredentials = false;
			for (const requirement of this.requirements) {
				// Credentials are stored in encoded format
				const credentialId = encodeURIComponent(requirement.credential.id);
				if (AuthForm.isSaveable(requirement) && this.storedCredentials[credentialId]) {
					retrievedCredentials = true;

					postData += `&${credentialId}=${
						this.authForm.isUserNameOrDomainRequirement(credentialId)
							? this.storedCredentials[credentialId]
							: await environment.decrypt(
									this.referenceUri,
									this.storedCredentials[credentialId],
									'ProtectData'
							  )
					}`;
				}
			}

			if (!retrievedCredentials) {
				return null;
			}

			postData += `&${encodeURIComponent(this.authForm.getSaveCredentialsID())}=true`;

			const singleButtonCredential = this.authForm.getSingleButtonCredential();
			postData += `&${encodeURIComponent(singleButtonCredential.id)}=${encodeURIComponent(
				singleButtonCredential.value
			)}`;

			return postData;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error in CredentialsManager getPostData: ' + e.stack,
				AotCategory.Error,
				'Failed to read saved credentials from local database'
			);
			return null;
		}
	}

	/**
	 * Returns POST data constructed from credentials fetched from the native in encoded format.
	 * example: "StateContext=&loginBtn=Log%20On&password=pw&saveCredentials=true&username=domain\name"
	 */
	async getPostDataWithFetchCredentialFromNative(stateContext: string): Promise<string> {
		try {
			const credential = await NativeBridgeWrapper.getCredentials(this.referenceUri);
			if (credential) {
				const fetchedCredential = JSON.parse(credential);
				const username = fetchedCredential.domain + '%5C' + fetchedCredential.username;
				const credentials: StringMap = {
					username: username,
					password: fetchedCredential.password,
				};

				let postData = `StateContext=${encodeURIComponent(stateContext)}`;

				let retrievedCredentials = false;
				for (const requirement of this.requirements) {
					// Credentials are stored in encoded format
					const credentialId = encodeURIComponent(requirement.credential.id);
					if (AuthForm.isSaveable(requirement) && credentials[credentialId]) {
						retrievedCredentials = true;

						postData += `&${credentialId}=${
							this.authForm.isUserNameOrDomainRequirement(credentialId)
								? credentials[credentialId]
								: await environment.decrypt(
										this.referenceUri,
										credentials[credentialId],
										'ProtectData'
								  )
						}`;
					}
				}

				if (!retrievedCredentials) {
					return null;
				}

				postData += `&${encodeURIComponent(this.authForm.getSaveCredentialsID())}=true`;

				const singleButtonCredential = this.authForm.getSingleButtonCredential();
				postData += `&${encodeURIComponent(
					singleButtonCredential.id
				)}=${encodeURIComponent(singleButtonCredential.value)}`;

				return postData;
			} else {
				return null;
			}
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				'Error in CredentialsManager getPostData: ' + e.stack
			);
			return null;
		}
	}

	async onAuthenticationSucceeded(postData: string): Promise<void> {
		if ((!config.rememberUsername && !this.allowSavedCredentials) || !postData) {
			return;
		}

		try {
			const postDataFields = await this.parse(postData);
			await this.prepopulatedCredentialsManager.onAuthenticationSucceeded(postDataFields);

			// If there was no save credentials option in UI, or user has not selected it
			if (
				!postDataFields[this.authForm.getSaveCredentialsID()] ||
				postDataFields[this.authForm.getSaveCredentialsID()].toLowerCase() !== 'true'
			) {
				return;
			}

			let updateSavedCredentials = false;
			for (const key of Object.keys(postDataFields)) {
				if (
					this.authForm.hasSaveID(key) &&
					(!this.storedCredentials[key] ||
						this.storedCredentials[key] !== postDataFields[key])
				) {
					updateSavedCredentials = true;
					this.storedCredentials[key] = postDataFields[key];
				}
			}

			if (updateSavedCredentials) {
				await this.credentialsStore.save(
					this.authServiceUrl,
					this.referenceUri,
					this.storedCredentials
				);
			}
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`Error in CredentialsManager onAuthenticationSucceeded: ${e.stack}`,
				AotCategory.Error,
				`Failed to save credentials to local database`
			);
		}
	}

	// Erase stored credentials when form sent by server does not have a savecredentials requirement, or a saveable requirement.
	// Erasing stored credentials on changing config is handled in eraseCredentialsNotAllowedByConfig().
	private async eraseInvalidStoredCredentials(): Promise<void> {
		if (!this.authForm.getSaveCredentialsID() && this.areCredentialsStored) {
			Logger.getInstance().write(
				logConstants.INFO,
				'CredentialsManager: Erasing stored credentials as form has no saveCredentials ID.'
			);
			await this.eraseCredentials();
		}
	}

	updateRequirements(requirements: ParsedRequirementNode[]): void {
		this.authForm = new AuthForm(requirements, this.allowSavedCredentials);
	}

	/**
	 * Returns the updated requirements after validation for saveCredentials checkbox.
	 */
	getUpdatedRequirements(): ParsedRequirementNode[] {
		return this.authForm.getRequirements();
	}

	async onAuthenticationFailed(): Promise<void> {
		// TODO: erase credentials only if credentials were sent for all saveable requirements, since auth failure might be due to incorrect PIN/token.
		if (this.allowSavedCredentials && this.areCredentialsStored) {
			Logger.getInstance().write(
				logConstants.INFO,
				'CredentialsManager: Authentication failed, deleting stored credentials.'
			);
			try {
				await this.eraseCredentials();
			} catch (e) {
				Logger.getInstance().write(
					logConstants.ERROR,
					'Error in CredentialsManager onAuthenticationFailed: ' + e.message,
					AotCategory.Error,
					'Failed to delete saved credentials from local database'
				);
			}
		}
	}

	private async parse(postData: string): Promise<StringMap> {
		const postDataFields: StringMap = {};
		const fields = postData.split('&');
		for (const field of fields) {
			const keyValuePair = field.split('=');
			// For optimization, encrypt only if requirement has a SaveID, and is not a username or domain requirement
			postDataFields[keyValuePair[0]] =
				this.authForm.hasSaveID(keyValuePair[0]) &&
				!this.authForm.isUserNameOrDomainRequirement(keyValuePair[0])
					? await environment.encrypt(this.referenceUri, keyValuePair[1], 'ProtectData')
					: keyValuePair[1];
		}
		return postDataFields;
	}

	private static canSaveCredentials(authServiceUrl: string): boolean {
		return (
			config.savePasswordMode === SavePasswordMode.Always ||
			(config.savePasswordMode === SavePasswordMode.SecureOnly &&
				authServiceUrl.startsWith('https://'))
		);
	}

	private async eraseCredentials(): Promise<void> {
		await this.credentialsStore.erase(this.authServiceUrl, this.referenceUri);
		this.storedCredentials = {};
		this.areCredentialsStored = false;
	}
}
